/**
 * Projector: the room clock, the event as it is read out, the join code in the
 * lobby, and the tables side by side at the debrief. Nothing hidden, nothing
 * from any one table's list.
 */
import { bookingStatusLabel } from '@nightshift/engine/client';
import { linkValid } from '../net/api.ts';
import { useNow, useSocket } from '../net/hooks.ts';
import type { Status } from '../net/socket.ts';
import { Art, PhaseGuide, phaseName, RESOURCE_TITLE } from '../ui/bits.tsx';
import { Calibration } from '../faculty/Debrief.tsx';
import type { ProjectorWire } from '../faculty/wire.ts';
import type { ReactElement } from 'react';

const PHASE_LINE: Record<string, string> = {
  ACT: 'Action. Plan care, arrange help and referrals, then press Ready.',
  LOG: 'Review. Read the results together. Control starts the next turn when the room is ready.',
  HANDOVER: '0700. Hand over every patient aloud. Each counts by how far through treatment they are.',
};

/** Show each table's visible shared resources without retired pressure metrics. */
export function ProjectorTables({ tables }: Pick<ProjectorWire, 'tables'>): ReactElement {
  return <footer className="proj-ring" aria-label="Shared resources by table">
    {tables.map(table => <div key={table.no} className={`proj-t ${table.divert ? 'divert' : ''}`} data-testid={`proj-table-${table.no}`}>
      <span className="proj-t-head"><b>Table {table.no}</b><span>{table.divert ? 'On divert' : table.bookings.length ? 'Resource bookings' : 'No resource bookings'}</span></span>
      <ProjectorBookings bookings={table.bookings} />
    </div>)}
  </footer>;
}

export function ProjectorApp({ token }: { token: string }) {
  const { sock, view, status } = useSocket<ProjectorWire>(`/ws?t=${encodeURIComponent(token)}`, () => linkValid(token));
  const now = useNow(250) + (sock?.offset ?? 0);
  if (status === 'denied') return <main className="proj proj-msg" role="alert">{sock?.reason ?? 'This projector link has expired.'}</main>;
  if (!view) return <main className="proj proj-msg">Connecting…</main>;
  const c = view.clock;

  if (c.phase === 'SETUP') return <ProjectorLobby view={view} />;
  if ((c.phase === 'DEBRIEF' || c.phase === 'ENDED') && view.debrief) return <ProjectorDebrief view={view} />;
  return <ProjectorLive view={view} status={status} now={now} />;
}

/** Keep lobby rendering independent of the live clock. */
function ProjectorLobby({ view }: { view: ProjectorWire }): ReactElement {
  const c = view.clock;
  return (
    <main className="proj proj-lobby">
      <Art name="hero" className="proj-bg" eager />
      <h1 className="display">{view.session.name}</h1>
      <PhaseGuide phase={c.phase} handoverTurn={c.handoverTurn} />
      <p className="proj-join">
        Go to <b>{location.host}</b> and enter
      </p>
      <p className="proj-code mono">{view.code}</p>
      <div className="proj-qrs">
        {view.tables.map((t) => (
          <figure key={t.no}>
            <img src={`/api/sessions/${view.sessionId}/qr/${t.no}`} alt={`Join Table ${t.no}`} width={180} height={180} />
            <figcaption>Table {t.no}</figcaption>
          </figure>
        ))}
      </div>
    </main>
  );
}

/** Render the existing completed-shift summary. */
function ProjectorDebrief({ view }: { view: ProjectorWire }): ReactElement {
  const c = view.clock;
  const d = view.debrief!;
  return (
    <main className="proj proj-debrief">
      <Art name={c.handoverTurn ? 'dawn' : 'hero'} className="proj-bg dim" eager />
      <h1 className="display">{c.handoverTurn ? '0700' : c.hour}</h1>
      <PhaseGuide phase={c.phase} handoverTurn={c.handoverTurn} />
      <div className="proj-tables">
        {d.tables.map((t) => (
          <article key={t.no}>
            <h2 className="display">Table {t.no}</h2>
            <p className="proj-nums">
              <span>
                <b className="display">{t.dashboard.saved}</b> saved
              </span>
              <span>
                <b className="display">{t.dashboard.harmed}</b> harmed
              </span>
              <span>
                <b className="display">{t.dashboard.died}</b> died
              </span>
            </p>
            <p className="muted">
              {t.dashboard.diverted} diverted, {t.dashboard.bounceBacks} bounced back, {t.objectives.filter((o) => o.achieved).length} of {t.objectives.length} objectives
            </p>
          </article>
        ))}
      </div>
      <div className="proj-split">
        <section>
          <h2 className="display">Were the numbers honest?</h2>
          <Calibration rows={d.calibration} />
        </section>
        {view.starred.length > 0 && (
          <section>
            <h2 className="display">What we saw</h2>
            <ul className="proj-notes">
              {view.starred.map((n, i) => (
                <li key={i}>
                  {n.table ? <span className="muted">Table {n.table}: </span> : null}
                  {n.text}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}

/** Keep the live presentation separate from its socket subscription. */
function ProjectorLive({ view, status, now }: { view: ProjectorWire; status: Status; now: number }): ReactElement {
  const c = view.clock;
  const left = c.paused ? (c.remainingMs ?? 0) : c.phaseEndsAt ? Math.max(0, c.phaseEndsAt - now) : null;
  const secs = left === null ? null : Math.ceil(left / 1000);
  const frac = left !== null && c.phaseMs ? Math.max(0, Math.min(1, left / c.phaseMs)) : 1;
  return (
    <main className={`proj proj-live phase-${c.phase}`}>
      {status !== 'open' && <div className="connbar">Reconnecting…</div>}
      <header className="proj-top">
        <span className="proj-hour display">{c.hour}</span>
        <span className="proj-phase">
          <strong>{phaseName(c.phase)}</strong>
          <span>
            Turn {c.turn} of {c.lastTurn}
          </span>
        </span>
        <span className={`proj-count ${secs !== null && secs <= 10 ? 'low' : ''}`}>
          {c.phase === 'LOG' ? 'Waiting for Control' : c.paused ? 'Paused' : secs !== null ? `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}` : ''}
        </span>
      </header>
      <div className="proj-bar" aria-hidden>
        <i style={{ transform: `scaleX(${frac})` }} />
      </div>
      <PhaseGuide phase={c.phase} handoverTurn={c.handoverTurn} />
      {view.broadcast && <p className="proj-broadcast">{view.broadcast}</p>}
      <section className="proj-body">
        {view.events.length > 0 && c.phase === 'ACT' && <p className="proj-now">{PHASE_LINE[c.phase]}</p>}
        {view.events.length > 0 && c.phase === 'ACT' ? (
          view.events.map((e) => (
            <article key={e.key} className="proj-event">
              <h2 className="display">{e.title}</h2>
              <p className="proj-flavour">{e.flavour}</p>
              <p className="proj-rule">{e.rule}</p>
            </article>
          ))
        ) : (
          <p className="proj-line display">{PHASE_LINE[c.phase] ?? ''}</p>
        )}
      </section>
      <ProjectorTables tables={view.tables} />
    </main>
  );
}

/** Makes each table's booked resources visible across the room. */
function ProjectorBookings({ bookings }: { bookings: ProjectorWire['tables'][number]['bookings'] }): ReactElement | null {
  if (bookings.length === 0) return null;
  return (
    <div aria-label="Booked resources">
      {bookings.map((b, i) => <p key={i}>{RESOURCE_TITLE[b.kind]}: turn {b.from}{b.until === null ? ' onward' : `–${b.until}`} · {bookingStatusLabel(b)}</p>)}
    </div>
  );
}
